import React, { useEffect, useMemo, useRef, useState } from 'react';

import isEqual from 'lodash/isEqual';

import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';

import FlatImageEditor from '~/components/Common/Images/FlatImageEditor';
import { mergeFlatItemsWithLegacy, toFlatImageItem } from '~/components/Common/Images/legacyImageAdapter';
import { FlatImageEditorProps, FlatImageItem } from '~/components/Common/Images/types';

import { useGetRoomImages } from '~/hooks/apis/accommodation/useGetRoomImages';
import { useUpdateRoomImages } from '~/hooks/apis/accommodation/useUpdateRoomImages';

import { AdminRoomImageResponse } from '~/services/AccommodationService';

interface Props {
  roomId: string;
  roomName: string;
  isExpanded: boolean;
}

function getDisplayOrder(image: Pick<AdminRoomImageResponse, 'order' | 'manualOrder'>) {
  return image.manualOrder ?? image.order;
}

function compareRoomImages<T extends Pick<AdminRoomImageResponse, 'order' | 'manualOrder' | 'imageId'>>(
  left: T,
  right: T,
) {
  const leftDisplayOrder = getDisplayOrder(left);
  const rightDisplayOrder = getDisplayOrder(right);

  if (leftDisplayOrder !== rightDisplayOrder) {
    return leftDisplayOrder - rightDisplayOrder;
  }

  if (left.order !== right.order) {
    return left.order - right.order;
  }

  return left.imageId.localeCompare(right.imageId);
}

function sortRoomImages<T extends Pick<AdminRoomImageResponse, 'order' | 'manualOrder' | 'imageId'>>(images: Array<T>) {
  return [...images].sort(compareRoomImages);
}

type EditableRoomImage = AdminRoomImageResponse & {
  id: string;
  publicImageId: string;
  hidden: boolean;
};

function toEditableRoomImage(image: AdminRoomImageResponse): EditableRoomImage {
  return {
    ...image,
    id: image.imageId,
    publicImageId: image.imageId,
    hidden: image.hidden ?? false,
  };
}

function applyDenseManualOrder<T extends EditableRoomImage>(images: Array<T>): Array<T> {
  return images.map((image, index) => ({
    ...image,
    manualOrder: index,
  }));
}

function getSourceLabel(image: EditableRoomImage) {
  if (image.source !== 'sync') {
    return 'Manual';
  }

  return image.sourceService ?? 'Sync';
}

function renderMetadataChips(image: EditableRoomImage) {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Chip size="small" label={`Source: ${getSourceLabel(image)}`} />
    </Stack>
  );
}

export default function AccommodationPropertyRoomImagesPanel({ roomId, roomName, isExpanded }: Props) {
  const { data, isLoading, error } = useGetRoomImages({
    roomId,
    enabled: isExpanded,
  });
  const updateRoomImagesMutation = useUpdateRoomImages();
  const [draftImages, setDraftImages] = useState<Array<EditableRoomImage>>([]);
  const hasInitialisedDraftImagesRef = useRef(false);

  const sortedImages = useMemo(() => sortRoomImages(draftImages), [draftImages]);
  const sortedPersistedImages = useMemo(
    () => sortRoomImages((data?.images ?? []).map(toEditableRoomImage)),
    [data?.images],
  );
  const hasChanges = !isEqual(sortedImages, sortedPersistedImages);
  const hiddenImagesCount = sortedImages.filter((image) => image.hidden).length;

  useEffect(() => {
    if (!data) {
      return;
    }

    if (!hasInitialisedDraftImagesRef.current || !hasChanges) {
      setDraftImages(data.images.map(toEditableRoomImage));
      hasInitialisedDraftImagesRef.current = true;
    }
  }, [data, hasChanges]);

  const handleItemsChange = (
    nextItems: Array<FlatImageItem>,
    meta?: Parameters<NonNullable<FlatImageEditorProps['onItemsChange']>>[1],
  ) => {
    setDraftImages((currentImages) => {
      const nextImages = mergeFlatItemsWithLegacy(currentImages, nextItems).map((image) => ({
        ...image,
        id: image.imageId,
        publicImageId: image.imageId,
        hidden: image.hidden ?? false,
      }));

      return meta?.reason === 'reorder' ? applyDenseManualOrder(nextImages) : nextImages;
    });
  };

  const handleSave = async () => {
    try {
      await updateRoomImagesMutation.mutateAsync({
        roomId,
        images: sortedImages.map((image) => ({
          imageId: image.imageId,
          title: image.title,
          hidden: image.hidden,
        })),
      });
    } catch {
      // Mutation-level error handling already surfaces feedback to the user.
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load room images: {error.message}</Alert>;
  }

  if (!data || sortedImages.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No internal room images are currently stored for {roomName}.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {sortedImages.length} persisted room images{hiddenImagesCount > 0 ? ` (${hiddenImagesCount} hidden)` : ''}.
        </Typography>

        <Button variant="contained" onClick={handleSave} disabled={!hasChanges || updateRoomImagesMutation.isPending}>
          {updateRoomImagesMutation.isPending ? 'Saving...' : 'Save changes'}
        </Button>
      </Stack>

      <Box>
        <FlatImageEditor
          deleteActionLabel="Delete image"
          descriptionLabel="Title"
          disabled={updateRoomImagesMutation.isPending}
          emptyStateMessage={`No internal room images are currently stored for ${roomName}.`}
          items={sortedImages.map((image) => toFlatImageItem(image))}
          renderItemActions={(item) => {
            const image = sortedImages.find((currentImage) => currentImage.imageId === String(item.id));

            return image ? renderMetadataChips(image) : null;
          }}
          reorderHiddenToEndOnToggle={false}
          showDeleteAction={false}
          showDescriptionField
          showHiddenToggle
          showReorderAction
          onItemsChange={handleItemsChange}
        />
      </Box>
    </Stack>
  );
}
